import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  CalendarRange,
  Clock3,
  FlagTriangleRight,
  MapPin,
  Route as RouteIcon,
  UsersRound,
} from "lucide-react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { formatCourseDate, formatCurrency, formatMinutes } from "@/lib/date"
import { staffCourseQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/staff/course")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(staffCourseQueryOptions()),
  head: () => ({
    meta: [{ title: "Course setup · Groundline staff" }],
  }),
  pendingComponent: () => <StaffPagePending kind="course" />,
  component: CourseContext,
})

function CourseContext() {
  const { data } = useSuspenseQuery(staffCourseQueryOptions())
  const busiest = [...data.days].sort((a, b) => b.occupancy - a.occupancy).at(0)

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <FlagTriangleRight className="size-4" />
            Operating context
          </span>
          <h1 className="staff-page-title">{data.course.shortName}.</h1>
          <p className="staff-page-copy flex items-center gap-1.5">
            <MapPin className="size-3.5" />
            {data.course.location}
          </p>
        </div>
      </header>

      <section className="ruled-panel overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">Course configuration</span>
            <p className="mt-1 text-xs text-muted-foreground">
              Read-only operational values for this demo deployment.
            </p>
          </div>
        </div>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-4">
          <CourseFact
            icon={RouteIcon}
            label="Round"
            value={`${data.course.holes} holes`}
          />
          <CourseFact
            icon={Clock3}
            label="First to last tee"
            value={`${formatMinutes(
              data.course.firstTeeMinutes
            )} – ${formatMinutes(data.course.lastTeeMinutes)}`}
          />
          <CourseFact
            icon={CalendarRange}
            label="Interval"
            value={`${data.course.intervalMinutes} minutes`}
          />
          <CourseFact
            icon={UsersRound}
            label="Capacity"
            value={`${data.course.maxPlayers} per time`}
          />
          <CourseFact
            icon={FlagTriangleRight}
            label="Green fee"
            value={formatCurrency(
              data.course.greenFeeCents,
              data.course.currency
            )}
          />
          <CourseFact
            icon={FlagTriangleRight}
            label="Shared cart"
            value={formatCurrency(
              data.course.cartFeeCents,
              data.course.currency
            )}
          />
          <CourseFact
            icon={MapPin}
            label="Timezone"
            value={data.course.timezone}
          />
          <CourseFact
            icon={CalendarRange}
            label="Busiest forecast"
            value={
              busiest
                ? `${formatCourseDate(busiest.date, {
                    weekday: "short",
                    month: "short",
                  })} · ${busiest.occupancy}%`
                : "No data"
            }
          />
        </dl>
      </section>

      <section className="ruled-panel mt-6 overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">Seven-day runway</span>
            <p className="mt-1 text-xs text-muted-foreground">
              Utilization is calculated from active booking player spots.
            </p>
          </div>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Open times</th>
                <th>Blocked</th>
                <th>Players</th>
                <th>Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((day) => (
                <tr key={day.date}>
                  <td className="font-semibold">
                    {formatCourseDate(day.date, {
                      weekday: "short",
                      month: "short",
                    })}
                  </td>
                  <td>{day.openTimes}</td>
                  <td>{day.blockedTimes}</td>
                  <td>{day.players}</td>
                  <td>
                    <div className="flex min-w-[150px] items-center gap-3">
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(100, day.occupancy)}%` }}
                        />
                      </span>
                      <strong className="w-10 text-right text-xs">
                        {day.occupancy}%
                      </strong>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

function CourseFact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof FlagTriangleRight
  label: string
  value: string
}) {
  return (
    <div className="border-b border-border p-5 sm:border-r">
      <dt className="flex items-center gap-1.5 text-[0.65rem] font-bold tracking-[0.1em] text-muted-foreground uppercase">
        <Icon className="size-3.5" />
        {label}
      </dt>
      <dd className="mt-2 text-sm font-semibold">{value}</dd>
    </div>
  )
}
